import React from 'react';
import type {SetRequired} from 'type-fest';
import {type FieldValues, type FieldPath} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';
import {
	RadiosField,
	type RadiosFieldProps,
} from '@netx/core-components/RadiosField';

export interface ReactHookFormRadiosFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<RadiosFieldProps, 'name'>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormRadiosFieldPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormRadiosFieldProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<RadiosField
					error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					ref={combineRefs(ref, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, checked) =>
							field.onChange(checked),
						),
					)}
				/>
			)}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `RadiosField` component
 */
export const ReactHookFormRadiosField = React.forwardRef<
	HTMLDivElement,
	ReactHookFormRadiosFieldProps
>(ReactHookFormRadiosFieldPrimitive) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormRadiosFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormRadiosField;
